import { useState } from 'react';

interface JobCardProps {
  job: {
    id: string;
    service_category: string;
    service_subcategory: string;
    city: string;
    postcode: string;
    description: string;
    is_instant: boolean;
    scheduled_date: string | null;
    created_at: string;
    images?: string[];
  };
  onViewDetails: (jobId: string) => void;
}

export default function JobCard({ job, onViewDetails }: JobCardProps) {
  const scheduledTime = job.scheduled_date ? new Date(job.scheduled_date) : null;
  const descriptionPreview = job.description?.substring(0, 120) || 'No description provided';
  const hasMoreText = job.description?.length > 120;

  return (
    <div className="bg-white rounded-xl shadow-sm hover:shadow-md transition-all p-6 border-2 border-gray-100 hover:border-[#ff751f]/30">
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex-1">
          <div className="flex items-center gap-3 mb-2">
            <h3 className="text-lg font-bold text-gray-900">{job.service_subcategory}</h3>
            {job.is_instant ? (
              <span className="px-3 py-1 bg-gradient-to-r from-red-500 to-orange-500 text-white text-xs font-bold rounded-full flex items-center gap-1 whitespace-nowrap">
                <i className="ri-flashlight-fill"></i>
                INSTANT - Priority
              </span>
            ) : (
              <span className="px-3 py-1 bg-blue-100 text-blue-800 text-xs font-semibold rounded-full whitespace-nowrap">
                SCHEDULED
              </span>
            )}
          </div>
          <p className="text-sm text-gray-600">{job.service_category}</p>
        </div>
      </div>

      {/* Location */}
      <div className="mb-4 flex items-center gap-2 text-gray-700">
        <i className="ri-map-pin-line text-[#ff751f]"></i>
        <span className="font-semibold">{job.city}</span>
        <span className="text-gray-400">•</span>
        <span className="text-sm">{job.postcode}</span>
      </div>

      {/* Timing */}
      {scheduledTime && (
        <div className="mb-4 flex items-center gap-2 text-gray-700">
          <i className="ri-calendar-line text-[#ff751f]"></i>
          <span className="text-sm">
            {scheduledTime.toLocaleDateString('en-GB', { 
              weekday: 'short', 
              day: 'numeric', 
              month: 'short' 
            })} at {scheduledTime.toLocaleTimeString('en-GB', { 
              hour: '2-digit', 
              minute: '2-digit' 
            })}
          </span>
        </div>
      )}

      {/* Description Preview */}
      <div className="mb-4 p-3 bg-gray-50 rounded-lg">
        <p className="text-sm text-gray-700">
          {descriptionPreview}
          {hasMoreText && <span className="text-[#ff751f]">...</span>}
        </p>
      </div>

      {/* Images Indicator */}
      {job.images && job.images.length > 0 && (
        <div className="mb-4 flex items-center gap-2 text-sm text-gray-600">
          <i className="ri-image-line text-[#ff751f]"></i>
          <span>{job.images.length} photo{job.images.length !== 1 ? 's' : ''} attached</span>
        </div>
      )}

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <p className="text-xs text-gray-500">
          Posted {new Date(job.created_at).toLocaleString('en-GB', {
            day: 'numeric',
            month: 'short',
            hour: '2-digit',
            minute: '2-digit'
          })}
        </p>
        <button
          onClick={() => onViewDetails(job.id)}
          className="px-6 py-2 bg-gradient-to-r from-[#ff751f] to-[#ff8c3a] text-white font-semibold rounded-lg hover:from-[#e56a0f] hover:to-[#ff751f] transition-all shadow-md hover:shadow-lg cursor-pointer whitespace-nowrap"
        >
          View Now
        </button>
      </div>
    </div>
  );
}
